import { useEffect, useState, useCallback } from "react";
import AppLayout from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, Car, DollarSign, Activity, Plus, Trash2, Radio, Pencil, Calendar as CalIcon, Clock, Archive } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import UserEditDialog from "@/components/UserEditDialog";
import AuthLogDialog from "@/components/AuthLogDialog";

// Naples-EDT month helpers. Admin dashboard's "Total viajes" / "Ingresos
// totales" reset every calendar month; the picker below lets the admin
// jump to any past month.
const NAPLES_TZ = "America/New_York";
const currentNaplesYearMonth = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: NAPLES_TZ, year: "numeric", month: "2-digit",
  }).formatToParts(new Date());
  const y = Number(parts.find((p) => p.type === "year").value);
  const m = Number(parts.find((p) => p.type === "month").value);
  return { year: y, month: m };
};
const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const monthLabel = (y, m) => `${MONTHS_ES[m - 1]} ${y}`;

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [authLogOpen, setAuthLogOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "dispatcher", phone: "", driver_pct: 70 });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  // Month picker state — defaults to CURRENT Naples month so "Ingresos
  // totales" starts empty on the 1st of each month, per user request.
  const [period, setPeriod] = useState(currentNaplesYearMonth);

  const load = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([
        api.get(`/stats/overview?year=${period.year}&month=${period.month}`),
        api.get("/users"),
      ]);
      setStats(s.data);
      setUsers(u.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.role !== "operator") delete payload.driver_pct;
      else payload.driver_pct = Number(payload.driver_pct);
      await api.post("/users", payload);
      toast.success("Usuario creado");
      setForm({ email: "", password: "", name: "", role: "dispatcher", phone: "", driver_pct: 70 });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const del = async (u) => {
    if (u.id === user.id) { toast.error("No puedes eliminarte a ti mismo"); return; }
    if (!window.confirm(`¿Eliminar a ${u.name || u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const kpi = [
    { label: "Viajes activos", value: stats?.active_trips ?? "-", icon: Radio, color: "text-primary" },
    { label: "Conductores libres", value: `${stats?.free_drivers ?? 0}/${stats?.total_drivers ?? 0}`, icon: Car, color: "text-emerald-400" },
  ];

  const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;

  return (
    <AppLayout>
      <div className="p-6 md:p-8 space-y-8 max-w-7xl mx-auto w-full">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-mono text-primary tracking-widest">ADMIN CONTROL</div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight" style={{ fontFamily: "Manrope" }}>Panel de administración</h1>
            <p className="text-muted-foreground mt-1 text-sm">Gestiona administradores, dispatchers y choferes.</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Month picker — controls the two big monthly KPIs below. Uses
                a native <input type="month"> so it works on mobile without
                extra dependencies. Value format is YYYY-MM. */}
            <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2">
              <CalIcon className="w-4 h-4 text-primary" />
              <input
                type="month"
                data-testid="admin-month-picker"
                className="bg-transparent text-sm font-mono text-white outline-none"
                value={`${period.year}-${String(period.month).padStart(2, "0")}`}
                onChange={(e) => {
                  const [y, m] = e.target.value.split("-");
                  if (y && m) setPeriod({ year: Number(y), month: Number(m) });
                }}
              />
              {!(stats?.is_current_month ?? true) && (
                <button
                  onClick={() => setPeriod(currentNaplesYearMonth())}
                  data-testid="admin-month-current"
                  className="text-[10px] font-mono uppercase tracking-widest text-primary hover:text-primary/80"
                  title="Volver al mes actual"
                >
                  Hoy
                </button>
              )}
            </div>
            <Button
              onClick={() => setAuthLogOpen(true)}
              variant="outline"
              className="border-primary/50 text-primary hover:bg-primary/10 font-bold mr-2"
              data-testid="open-auth-log-button"
              title="Historial de conexiones y horas trabajadas"
            >
              <Clock className="w-4 h-4 mr-1" /> Horas conexión
            </Button>
            <Button
              onClick={async () => {
                const cutoff = window.prompt(
                  "⚠️  ACCIÓN DESTRUCTIVA\n\nEsto inserta un settlement de ajuste que cierra a $0 el saldo pre-corte de CADA chofer.\n\nFecha de corte (YYYY-MM-DD):",
                  "2026-08-14"
                );
                if (!cutoff) return;
                if (!window.confirm(`Confirmá cerrar libros pre-${cutoff} para TODOS los choferes.\n\nEs idempotente (podés correrlo varias veces sin duplicar).`)) return;
                try {
                  const { data } = await api.post("/admin/close-books-before", { cutoff_date: cutoff });
                  const touched = (data.results || []).filter(r => r.adjustment !== 0 && !r.skipped);
                  const skipped = (data.results || []).filter(r => r.skipped).length;
                  toast.success(
                    `Libros cerrados pre-${cutoff}. Ajustes creados: ${touched.length}${skipped ? `, ya cerrados: ${skipped}` : ""}`,
                    { duration: 8000 }
                  );
                } catch (err) {
                  toast.error(formatApiError(err));
                }
              }}
              variant="outline"
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 font-bold mr-2"
              data-testid="close-books-button"
              title="Cerrar libros contables antes de una fecha (ajuste automático por chofer)"
            >
              <Archive className="w-4 h-4 mr-1" /> Cerrar libros
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold" data-testid="new-user-button">
                <Plus className="w-4 h-4 mr-1" /> Nuevo usuario
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border" data-testid="new-user-dialog">
              <DialogHeader>
                <DialogTitle>Crear usuario</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label>Nombre</Label>
                  <Input data-testid="new-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label>Email</Label>
                  <Input data-testid="new-user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label>Contraseña</Label>
                  <Input data-testid="new-user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label>Rol</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="new-user-role" className="bg-input border-border"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="dispatcher">Dispatcher</SelectItem>
                      <SelectItem value="operator">Chofer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Teléfono (opcional)</Label>
                  <Input data-testid="new-user-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                {form.role === "operator" && (
                  <div className="space-y-1 pt-2 border-t border-border">
                    <Label>Comisión del chofer (%)</Label>
                    <div className="flex gap-2 items-center flex-wrap">
                      {[50, 60, 65, 70, 80].map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setForm({ ...form, driver_pct: p })}
                          data-testid={`new-user-pct-${p}`}
                          className={`text-xs font-mono font-bold px-3 py-1.5 rounded-full border transition-colors ${
                            Number(form.driver_pct) === p
                              ? "bg-primary text-primary-foreground border-primary"
                              : "border-border text-muted-foreground hover:text-white"
                          }`}
                        >
                          {p}/{100 - p}
                        </button>
                      ))}
                      <Input
                        data-testid="new-user-driver-pct"
                        type="number" min="0" max="100" step="1"
                        value={form.driver_pct}
                        onChange={(e) => setForm({ ...form, driver_pct: e.target.value })}
                        className="w-20 bg-input border-border font-mono"
                      />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Chofer <span className="text-emerald-400 font-mono">{form.driver_pct}%</span> · LLC <span className="text-cyan-400 font-mono">{100 - Number(form.driver_pct || 0)}%</span>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button
                  onClick={create}
                  disabled={busy || !form.email || !form.password || !form.name}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
                  data-testid="new-user-submit"
                >
                  {busy ? "Creando..." : "Crear"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Total viajes (con breakdown) */}
          <Card className="p-5 bg-card border-border lg:col-span-1" data-testid="kpi-trips-card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
                Viajes · {monthLabel(period.year, period.month)}
              </div>
              <Activity className="w-4 h-4 text-white" />
            </div>
            <div className="text-3xl font-black font-mono text-white" data-testid="kpi-total-trips">
              {stats?.total_trips ?? "-"}
            </div>
            {stats?.is_current_month !== false && (
              <div className="mt-3 grid grid-cols-2 gap-2 pt-3 border-t border-border">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Hoy</div>
                  <div className="text-lg font-bold font-mono text-emerald-400" data-testid="kpi-trips-today">
                    {stats?.trips_today ?? "-"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Semana</div>
                  <div className="text-lg font-bold font-mono text-cyan-400" data-testid="kpi-trips-week">
                    {stats?.trips_week ?? "-"}
                  </div>
                </div>
              </div>
            )}
          </Card>

          {kpi.map((k) => {
            const Icon = k.icon;
            return (
              <Card key={k.label} className="p-5 bg-card border-border">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">{k.label}</div>
                  <Icon className={`w-4 h-4 ${k.color}`} />
                </div>
                <div className={`text-3xl font-black font-mono ${k.color}`}>{k.value}</div>
              </Card>
            );
          })}

          {/* Ingresos (con breakdown) */}
          <Card className="p-5 bg-card border-border lg:col-span-1" data-testid="kpi-revenue-card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
                Ingresos · {monthLabel(period.year, period.month)}
              </div>
              <DollarSign className="w-4 h-4 text-amber-400" />
            </div>
            <div className="text-3xl font-black font-mono text-amber-400" data-testid="kpi-total-revenue">
              {fmt$(stats?.revenue)}
            </div>
            {stats?.is_current_month !== false && (
              <div className="mt-3 grid grid-cols-2 gap-2 pt-3 border-t border-border">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Hoy</div>
                  <div className="text-lg font-bold font-mono text-emerald-400" data-testid="kpi-revenue-today">
                    {fmt$(stats?.revenue_today)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Semana</div>
                  <div className="text-lg font-bold font-mono text-cyan-400" data-testid="kpi-revenue-week">
                    {fmt$(stats?.revenue_week)}
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Users table */}
        <Card className="bg-card border-border overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            <div className="font-bold" style={{ fontFamily: "Manrope" }}>Usuarios</div>
            <div className="text-xs text-muted-foreground font-mono ml-auto">{users.length} total</div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Comisión</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className="border-border" data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="font-mono text-xs">{u.email}</TableCell>
                    <TableCell>
                      <Badge className={
                        u.role === "admin" ? "bg-primary text-primary-foreground" :
                        u.role === "dispatcher" ? "bg-accent text-accent-foreground" :
                        "bg-emerald-500 text-white"
                      }>{u.role === "operator" ? "chofer" : u.role}</Badge>
                    </TableCell>
                    <TableCell>
                      {u.role === "operator" ? (
                        <span className="font-mono text-xs">
                          <span className="text-emerald-400 font-bold">{u.driver_pct ?? 70}%</span>
                          <span className="text-muted-foreground"> / </span>
                          <span className="text-cyan-400 font-bold">{100 - (u.driver_pct ?? 70)}%</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">{u.phone || "—"}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => toggleActive(u)}
                        data-testid={`toggle-user-${u.id}`}
                        className={`text-xs font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                          u.active ? "border-emerald-500 text-emerald-400" : "border-muted text-muted-foreground"
                        }`}
                      >
                        {u.active ? "Activo" : "Inactivo"}
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setEditing(u); setEditOpen(true); }}
                          data-testid={`edit-user-${u.id}`}
                          className="border-border"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => del(u)}
                          data-testid={`delete-user-${u.id}`}
                          className="border-border text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
      <UserEditDialog open={editOpen} onOpenChange={setEditOpen} user={editing} onSaved={load} />
      <AuthLogDialog open={authLogOpen} onOpenChange={setAuthLogOpen} />
    </AppLayout>
  );
}
